/** epicPLY global contact form. No HTML from triggers or endpoint responses is inserted. */
import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

const categories = { cooperation: 'Kooperation', withdrawal: 'Widerruf', 'color-request': 'Farben' };
const categoryValues = new Set(['Produkt', 'Bestellung', 'Reklamation', 'Widerruf', 'Kooperation', 'Sonstiges', 'Farben']);
const modeForCategory = (category) => Object.keys(categories).find((mode) => categories[mode] === category) || 'general';
const variantSelections = new WeakMap();
const clean = (value) => String(value ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, 1000);
const normalizeId = (value) => clean(value).replace(/^gid:\/\/shopify\/(Product|ProductVariant)\//, '');

// Horizon publishes the section-fetch promise synchronously, before the hidden variant ID updates.
document.addEventListener(StandardEvents.productSelect, (event) => {
  const scope = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  if (!scope || !event.promise) return;
  // Handle rejections immediately; cancelled variant fetches must not cause unhandled rejections.
  variantSelections.set(scope, event.promise.then((result) => result, () => null));
});

function contextFromParameters(parameters) {
  const context = { mode: parameters.get('contact-mode') || 'general', category: parameters.get('contact-category') || '', url: location.href };
  for (const key of ['product-id', 'product-handle', 'product-title', 'variant-id', 'variant-title', 'source', 'subject']) {
    const value = parameters.get(key);
    if (value) context[key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())] = clean(value);
  }
  return context;
}

async function triggerContext(trigger) {
  const context = { mode: trigger.dataset.contactMode || 'general', category: trigger.dataset.contactCategory || '', url: location.href };
  for (const key of ['productId', 'productHandle', 'productTitle', 'variantId', 'variantTitle', 'source', 'subject']) {
    if (trigger.dataset[key]) context[key] = clean(trigger.dataset[key]);
  }
  // Extension API: data-contact-context-order-number -> hidden contact_context[orderNumber].
  for (const [key, value] of Object.entries(trigger.dataset)) {
    if (key.startsWith('contactContext') && key.length > 14) {
      const name = key.slice(14);
      context[name[0].toLowerCase() + name.slice(1)] = clean(value);
    }
  }
  const scope = trigger.closest('.shopify-section');
  const picker = scope?.querySelector('variant-picker') || Array.from(document.querySelectorAll('variant-picker'))
    .find((element) => context.productId && element.dataset.productId === context.productId);
  const productScope = picker?.closest('.shopify-section') || scope;
  let selection = productScope && variantSelections.get(productScope);
  let result;
  while (selection) {
    result = await selection;
    const latest = variantSelections.get(productScope);
    if (latest === selection) break;
    selection = latest;
  }
  const currentPicker = productScope?.querySelector('variant-picker') || picker;
  if (currentPicker) {
    context.productId = clean(currentPicker.dataset.productId);
    context.productHandle = clean(currentPicker.dataset.productHandle);
    context.productTitle = clean(currentPicker.dataset.productTitle);
    let variant = result?.detail?.resource;
    if (!selection) {
      try { variant = JSON.parse(currentPicker.querySelector('script[type="application/json"]')?.textContent || 'null'); }
      catch { variant = null; }
    }
    // A null result indicates an unavailable combination or failed request. Never use a stale ID.
    context.variantId = normalizeId(variant?.id);
    context.variantTitle = clean(variant?.title);
    if (!variant) {
      context.variantTitle = Array.from(currentPicker.querySelectorAll('input:checked, option:checked'))
        .map((option) => clean(option.value)).join(' / ');
    }
  }
  return context;
}

class EpicplyContactForm extends HTMLElement {
  #listeners;
  #request;
  #context = {};
  #initialState = '';
  #submitted = false;
  busy = false;

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.form = this.querySelector('form');
    this.messages = JSON.parse(this.querySelector('[data-contact-messages]').textContent);
    this.form.noValidate = true;
    this.form.addEventListener('submit', this.#submit, options);
    this.form.addEventListener('invalid', this.#invalid, { ...options, capture: true });
    this.form.addEventListener('input', this.#input, options);
    this.form.addEventListener('change', this.#change, options);
    const parameters = new URLSearchParams(location.search);
    const context = this.closest('dialog') ? {} : contextFromParameters(parameters);
    context.mode = parameters.has('contact-mode') && !this.closest('dialog') ? context.mode : this.dataset.mode;
    if (!context.category && !parameters.has('contact-mode')) context.category = this.dataset.category;
    this.prepare(context);
    // Retain the previous subject/body URL-prefill feature, using values only.
    if (!this.closest('dialog') && parameters.has('body')) {
      this.form.elements.description.value = parameters.get('body').slice(0, 20000);
    }
    this.#initialState = this.#snapshot();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
  }

  prepare(context = {}) {
    if (this.busy) return false;
    this.form.reset();
    this.#clearErrors();
    this.querySelector('[data-form-fields]').hidden = false;
    this.querySelector('[data-success]').hidden = true;
    this.querySelector('[data-status]').textContent = '';
    this.#context = Object.fromEntries(Object.entries(context)
      .filter(([key]) => /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/.test(key))
      .map(([key, value]) => [key, clean(value)]));
    const initialCategory = categoryValues.has(context.category) ? context.category : categories[context.mode] || '';
    this.#context.mode = modeForCategory(initialCategory);
    this.#context.category = initialCategory;
    this.#context.url = clean(context.url || location.href);
    this.form.elements.startTime.value = String(Date.now());
    this.form.elements.contact_mode.value = this.#context.mode;
    this.form.elements.category.value = initialCategory;
    const container = this.querySelector('[data-context-fields]');
    container.replaceChildren();
    for (const [key, value] of Object.entries(this.#context)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `contact_context[${key}]`;
      input.value = value;
      container.append(input);
    }
    this.#syncCategory();
    this.#submitted = false;
    this.#initialState = this.#snapshot();
    return true;
  }

  #syncCategory() {
    const category = this.form.elements.category.value;
    this.#context.category = category;
    this.#context.mode = modeForCategory(category);
    this.form.elements.contact_mode.value = this.#context.mode;
    for (const key of ['mode', 'category']) {
      const input = this.form.elements.namedItem(`contact_context[${key}]`);
      if (input) input.value = this.#context[key];
    }
    for (const fields of this.querySelectorAll('[data-category-fields]')) {
      const active = fields.dataset.categoryFields === category;
      fields.hidden = !active;
      fields.disabled = !active;
      if (!active) {
        for (const input of fields.querySelectorAll('[name]')) this.#clearFieldError(input.name);
      }
    }
    this.#syncColor();
  }

  #snapshot() {
    return JSON.stringify(Array.from(this.form.elements)
      .filter((input) => input.matches('input:not([type="hidden"]), select, textarea') && input.name !== 'honeypot')
      .map((input) => [input.name || input.id, input.value, input.checked ?? null]));
  }

  get hasUnsavedChanges() {
    return !this.busy && !this.#submitted && this.#snapshot() !== this.#initialState;
  }

  get needsCloseConfirmation() {
    return !this.#submitted && this.#snapshot() !== this.#initialState;
  }

  #syncColor() {
    const custom = this.form.elements.color_choice.value === 'custom';
    const fields = this.querySelector('[data-custom-color]');
    fields.hidden = !custom;
    fields.disabled = !custom;
    this.form.elements.color_hex.required = custom;
  }

  #change = (event) => {
    if (event.target.name === 'category') this.#syncCategory();
    if (event.target.name === 'color_choice') {
      this.#syncColor();
      this.#clearFieldError('color_choice');
    }
  };

  #input = (event) => {
    const input = event.target;
    if (input.matches('[data-color-picker]')) this.form.elements.color_hex.value = input.value.toUpperCase();
    if (input.name === 'color_hex' && /^#[0-9a-f]{6}$/i.test(input.value)) {
      this.querySelector('[data-color-picker]').value = input.value;
    }
    if (input.name) this.#clearFieldError(input.name);
  };

  #invalid = (event) => {
    const input = event.target;
    input.setAttribute('aria-invalid', 'true');
    const error = Array.from(this.querySelectorAll('[data-field-error]')).find((element) => element.dataset.fieldError === input.name);
    if (error) error.textContent = input.validationMessage;
  };

  #clearFieldError(name) {
    for (const input of this.form.elements) if (input.name === name) input.removeAttribute('aria-invalid');
    for (const error of this.querySelectorAll('[data-field-error]')) if (error.dataset.fieldError === name) error.textContent = '';
  }

  #clearErrors() {
    for (const input of this.form.elements) input.removeAttribute('aria-invalid');
    for (const error of this.querySelectorAll('[data-field-error]')) error.textContent = '';
  }

  #status(text) {
    const status = this.querySelector('[data-status]');
    status.textContent = text;
    status.focus();
  }

  #submit = async (event) => {
    event.preventDefault();
    if (this.busy) return;
    this.#syncCategory();
    this.#clearErrors();
    for (const name of ['firstname', 'lastName', 'email', 'description', 'withdrawal_order_number']) {
      const input = this.form.elements.namedItem(name);
      input.value = input.value.trim();
    }
    if (!this.form.reportValidity()) return;
    if (Date.now() - Number(this.form.elements.startTime.value) < 10000) {
      this.#status(this.messages.wait);
      return;
    }
    const data = new FormData(this.form);
    // Unknown WeClapp fields are not guaranteed to be persisted. Include the context in description too.
    const lines = Object.entries(this.#context).filter(([, value]) => value).map(([key, value]) => `${key}: ${value}`);
    for (const input of this.querySelectorAll('[data-category-fields]:not([disabled]) [name]')) {
      if (!input.matches(':disabled') && input.value && /^(cooperation_|withdrawal_)/.test(input.name)) {
        lines.push(`${input.labels?.[0]?.textContent.trim() || input.name}: ${clean(input.value)}`);
      }
    }
    if (this.#context.mode === 'color-request') {
      const choice = this.form.elements.color_choice.value;
      const hex = (choice === 'custom' ? this.form.elements.color_hex.value : choice).toUpperCase();
      data.set('color_hex', hex);
      data.set('color_swatch_hex', hex);
      lines.push(`Wunschfarbe: ${hex}`);
      if (data.get('color_reference')) lines.push(`Farbbezeichnung: ${clean(data.get('color_reference'))}`);
    }
    data.set('description', `${data.get('description')}\n\n--- epicPLY Anfragekontext ---\n${lines.join('\n')}`);
    this.busy = true;
    this.querySelector('[data-form-fields]').inert = true;
    this.form.setAttribute('aria-busy', 'true');
    const button = this.form.querySelector('[type="submit"]');
    button.disabled = true;
    this.#status(this.messages.sending);
    this.#request = new AbortController();
    try {
      const endpoint = new URL(this.form.action);
      if (endpoint.protocol !== 'https:' && endpoint.origin !== location.origin) throw new Error('Endpoint');
      const response = await fetch(endpoint.href, {
        method: 'POST', body: data, credentials: 'omit', signal: this.#request.signal,
      });
      const payload = await response.json();
      if (!response.ok || payload?.status !== 'success') throw new Error('Response');
      this.querySelector('[data-form-fields]').hidden = true;
      this.querySelector('[data-status]').textContent = '';
      const success = this.querySelector('[data-success]');
      this.#submitted = true;
      success.hidden = false;
      success.focus();
      this.dispatchEvent(new CustomEvent('epicply:contact-success', { bubbles: true }));
    } catch (error) {
      if (error.name !== 'AbortError') this.#status(this.messages.error);
    } finally {
      this.busy = false;
      this.querySelector('[data-form-fields]').inert = false;
      button.disabled = false;
      this.form.removeAttribute('aria-busy');
      this.#request = undefined;
    }
  };
}

class EpicplyContactModal extends DialogComponent {
  #opener;
  #closing = false;
  #opening = false;
  #listeners;

  constructor() {
    super();
    // Horizon defines closeDialog as an instance field, not a prototype method.
    const horizonClose = this.closeDialog;
    this.closeDialog = async () => {
      if (this.#closing || !this.refs.dialog.open) return;
      const form = this.querySelector('epicply-contact-form');
      if (form?.needsCloseConfirmation && !window.confirm(form.busy ? form.messages.closeSending : form.messages.discard)) return;
      this.#closing = true;
      try { await horizonClose(); }
      finally { this.#closing = false; }
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('dialog:open', () => {
      this.#opening = false;
      const form = this.querySelector('epicply-contact-form');
      const target = form?.busy ? form.querySelector('[data-status]') : this.querySelector('input[name="firstname"]');
      target?.focus();
    }, options);
    this.addEventListener('dialog:close', () => {
      if (!this.querySelector('epicply-contact-form')?.busy) this.querySelector('epicply-contact-form')?.prepare();
      if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
      this.#opener = undefined;
    }, options);
    this.refs.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeDialog();
    }, options);
    // Native modal inertness confines focus; explicitly cycle Tab at the visible boundaries.
    this.refs.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.refs.dialog.querySelectorAll('a[href], button:enabled, input:enabled:not([type="hidden"]), select:enabled, textarea:enabled, [tabindex="0"]'))
        .filter((element) => element.getClientRects().length && !element.closest('[inert]'));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    }, options);
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  open(context, trigger) {
    if (this.#closing || this.#opening || this.refs.dialog.open) return;
    const mount = this.querySelector('[data-contact-mount]');
    if (!mount.firstElementChild) mount.append(this.querySelector('template').content.cloneNode(true));
    const form = mount.querySelector('epicply-contact-form');
    // Closing during submission is allowed; reopening preserves that pending request until it completes.
    if (!form.busy) {
      form.prepare(context);
    }
    this.#opener = trigger;
    this.#opening = true;
    this.showDialog();
  }

}

if (!customElements.get('epicply-contact-form')) customElements.define('epicply-contact-form', EpicplyContactForm);
if (!customElements.get('epicply-contact-modal')) customElements.define('epicply-contact-modal', EpicplyContactModal);

let openingSequence = 0;
document.addEventListener('click', async (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-epicply-contact-open]') : null;
  if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const modal = document.querySelector('epicply-contact-modal');
  if (!modal || typeof modal.open !== 'function' || typeof modal.refs.dialog.showModal !== 'function') return;
  event.preventDefault();
  const sequence = ++openingSequence;
  trigger.setAttribute('aria-busy', 'true');
  try {
    const context = await triggerContext(trigger);
    if (sequence === openingSequence && modal.isConnected) modal.open(context, trigger);
  } finally { trigger.removeAttribute('aria-busy'); }
});
